/**
 * ConfidencePanel — designed rendering of `FinalDecisionCompact.confidence_summary`.
 *
 * The summary's sole producer is `_approval_summary()` (capa 4), so it is only
 * populated on APPROVED decisions — on HUMAN_REVIEW / REJECTED expedientes it
 * arrives as `{}` and the panel explains that instead of looking broken. Known
 * keys get typed es-MX renderers; unknown keys fall back to the generic
 * key/value row so new engine keys degrade gracefully.
 *
 * Keys (see src/vera/pipeline/capa4_decision.py):
 *   face_match:            "HIGH" | "MEDIUM" | "LOW" | "FAILED"   (level, not score)
 *   name_comparisons:      { ine_vs_cert } (Levenshtein)
 *   ine_validation_source: "RENAPO" | "INTERNAL"
 *
 * `certificate_path` and `document_quality` were retired (#290): both were
 * constants (single certificate path; legibility relabeled as a score) that a
 * reviewer would read as evidence. Records sealed before the retirement still
 * carry them, so they are actively suppressed here — never rendered, not even
 * through the unknown-key fallback.
 */

import { Box, Card, CardContent, Stack, Typography } from "@mui/material";

import { statusTokens } from "../../theme";
import { FieldRow } from "./ExtractedDataCard";

const KNOWN_KEYS = new Set([
  "face_match",
  "name_comparisons",
  "ine_validation_source",
]);

// Retired constants (#290) still present on records sealed before the
// retirement. Suppressed so they can't resurface via the unknown-key fallback.
const RETIRED_KEYS = new Set(["certificate_path", "document_quality"]);

// ── face_match: a level, not a score ─────────────────────────────────────────

const FACE_LEVEL: Record<string, { label: string; segments: number; color: string }> = {
  HIGH: { label: "Alta", segments: 3, color: statusTokens.aprobado.ink },
  MEDIUM: { label: "Media", segments: 2, color: statusTokens.pendiente.ink },
  LOW: { label: "Baja", segments: 1, color: statusTokens.rechazado.ink },
  FAILED: { label: "Sin comparación", segments: 0, color: statusTokens.rechazado.ink },
};

function FaceMatchRow({ value }: { value: string }) {
  const level = FACE_LEVEL[value];
  if (!level) return <FieldRow label="Coincidencia facial" text={value} muted={false} />;
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Coincidencia facial
      </Typography>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Stack direction="row" spacing={0.375} aria-hidden>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 14,
                height: 6,
                borderRadius: 0.5,
                bgcolor: i < level.segments ? level.color : "grey.200",
              }}
            />
          ))}
        </Stack>
        <Typography variant="body2">{level.label}</Typography>
      </Stack>
    </Box>
  );
}

// ── name_comparisons: Levenshtein distances ──────────────────────────────────

const NAME_PAIR_LABEL: Record<string, string> = {
  ine_vs_cert: "INE vs certificado",
};

function NameComparisons({ value }: { value: Record<string, unknown> }) {
  const entries = Object.entries(value);
  if (entries.length === 0) return null;
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Comparación de nombres
      </Typography>
      <Stack spacing={0.5} sx={{ pl: 1.5 }}>
        {entries.map(([pair, dist]) => (
          <FieldRow
            key={pair}
            label={NAME_PAIR_LABEL[pair] ?? pair}
            text={
              typeof dist === "number"
                ? dist === 0
                  ? "exacto"
                  : `distancia ${dist}`
                : String(dist)
            }
            muted={false}
            numeric
          />
        ))}
      </Stack>
    </Box>
  );
}

// ── generic fallback (unknown keys) ──────────────────────────────────────────

function fallbackText(raw: unknown): string {
  if (typeof raw === "number") return Number.isInteger(raw) ? String(raw) : raw.toFixed(3);
  if (typeof raw === "string" || typeof raw === "boolean") return String(raw);
  return JSON.stringify(raw);
}

// ── panel ────────────────────────────────────────────────────────────────────

export function ConfidencePanel({ summary }: { summary: Record<string, unknown> }) {
  const entries = Object.entries(summary ?? {}).filter(([k]) => !RETIRED_KEYS.has(k));
  const unknown = entries.filter(([k]) => !KNOWN_KEYS.has(k));

  const faceMatch = summary?.face_match;
  const nameComparisons = summary?.name_comparisons;
  const ineSource = summary?.ine_validation_source;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h3" gutterBottom>
          Confianza
        </Typography>

        {entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Resumen de confianza disponible solo en aprobaciones automáticas.
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {typeof faceMatch === "string" && <FaceMatchRow value={faceMatch} />}

            {typeof ineSource === "string" && (
              <FieldRow
                label="Validación de INE"
                text={
                  ineSource === "RENAPO"
                    ? "Padrón RENAPO"
                    : ineSource === "INTERNAL"
                      ? "Verificación interna (MRZ)"
                      : ineSource
                }
                muted={false}
              />
            )}

            {nameComparisons !== null &&
              typeof nameComparisons === "object" &&
              !Array.isArray(nameComparisons) && (
                <NameComparisons value={nameComparisons as Record<string, unknown>} />
              )}

            {unknown.map(([key, raw]) => (
              <FieldRow key={key} label={key} text={fallbackText(raw)} muted={false} numeric />
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
